<template>
	<v-row class="full-height pending-approvals-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="pending-approvals-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search pending approvals..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="pending-approvals-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<v-col cols="6" sm="3" md="2" v-if="canFilterByRep">
							<v-select
								v-model="selectedSalesRepIds"
								:items="salesRepOptions"
								item-text="name"
								item-value="salesRepId"
								label="Sales Rep"
								multiple
								clearable
								outlined
								dense
								hide-details
								@change="onFilterChange"
								@click:clear="onFilterClear"
							>
								<template v-slot:selection="{ item, index }">
									<span v-if="index === 0" class="text-truncate">{{ item.name }}</span>
									<span v-if="index === 1" class="grey--text caption ml-1">
										+{{ selectedSalesRepIds.length - 1 }}
									</span>
								</template>
							</v-select>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 pending-approvals-table-container">
			<v-data-table
				:headers="headers"
				:items="quotes"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="pending-approvals-data-table"
				@click:row="navigateToQuote"
				must-sort
				fixed-header
			>
				<!-- Organization Column -->
				<template v-slot:item.organization="{ item }">
					<div class="d-flex align-center">
						<truncate-tooltip>{{ item.quoteCustomer.customer.organization }}</truncate-tooltip>
						<v-btn
							v-if="canEditCustomer"
							icon
							x-small
							class="ml-1 edit-customer-btn"
							@click.stop="navigateToCustomer(item.quoteCustomer.customerId)"
							title="Edit Customer"
						>
							<v-icon x-small>mdi-open-in-new</v-icon>
						</v-btn>
					</div>
				</template>

				<!-- Created Date Column -->
				<template v-slot:item.createdAt="{ item }">
					{{ formatDate(item.createdAt) }}
				</template>

				<!-- Sales Rep Column -->
				<template v-slot:item.salesRepId="{ item }">
					{{ repSalesRepInitials(item.salesRepId) }}
				</template>

				<!-- Status Column -->
				<template v-slot:item.status="{ item }">
					<div class="table-status" :class="renderStatusColor(item.status)">
						{{ formatStatus(item.status) }}
					</div>
				</template>

				<!-- Total Column -->
				<template v-slot:item.total="{ item }">
					{{ renderPrice(item.computedTotal ?? item.total) }}
				</template>

				<!-- Balance Column -->
				<template v-slot:item.balance="{ item }">
					{{ renderPrice(item.computedBalance ?? item.balance) }}
				</template>

				<!-- Contact Column -->
				<template v-slot:item.primaryContact="{ item }">
					<truncate-tooltip v-if="item.quoteCustomer.contact">
						{{ item.quoteCustomer.contact.firstName }} {{ item.quoteCustomer.contact.lastName }}
					</truncate-tooltip>
				</template>

				<!-- Email Column -->
				<template v-slot:item.email="{ item }">
					<truncate-tooltip v-if="item.quoteCustomer.contact && item.quoteCustomer.contact.email">
						{{ item.quoteCustomer.contact.email }}
					</truncate-tooltip>
				</template>

				<!-- Ref # Column -->
				<template v-slot:item.referenceNumber="{ item }">
					<truncate-tooltip v-if="item.referenceNumber">
						{{ item.referenceNumber }}
					</truncate-tooltip>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-file-document-outline</v-icon>
						<p class="mt-2 grey--text">No pending approvals found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>
	</v-row>
</template>

<script>
import { renderPrice } from "@/helpers/PricingCalculator";
import { SystemPermissions, WellKnownRoles } from "@/models/User";
import queries from "../../gql/queries";
import TruncateTooltip from "@/components/TruncateTooltip.vue";
export default {
	name: "PendingApprovalsList",
	components: { TruncateTooltip },
	data() {
		return {
			canEditCustomer: false,
			canFilterByRep: false,
			loading: true,
			searchForm: {
				keywords: "",
			},
			quotes: [],
			salesReps: [],
			totalCount: 0,
			selectedSalesRepIds: [],
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["modifiedAt"],
				sortDesc: [true],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "School/Organization",
					value: "organization",
					sortable: true,
					width: "14%",
				},
				{
					text: "Quote #",
					value: "quoteNumber",
					sortable: true,
					width: "6%",
				},
				{
					text: "Order Date",
					value: "createdAt",
					sortable: true,
					width: "8%",
				},
				{
					text: "Rep",
					value: "salesRepId",
					sortable: true,
					width: "4%",
				},
				{
					text: "Status",
					value: "status",
					sortable: true,
					width: "12%", // fits the uppercase PENDING APPROVAL chip (#300)
					align: "center",
				},
				{
					text: "Contact",
					value: "primaryContact",
					sortable: true,
					width: "10%",
				},
				{
					text: "Email",
					value: "email",
					sortable: true,
					width: "11%",
				},
				{
					text: "Ref #",
					value: "referenceNumber",
					sortable: true,
					width: "11%",
				},
				{
					text: "Order Total",
					value: "total",
					sortable: true,
					width: "7%",
					align: "end", // money reads right-aligned (#357)
				},
				{
					text: "Balance",
					value: "balance",
					sortable: true,
					width: "7%",
					align: "end", // money reads right-aligned (#357)
				},
			],
		};
	},
	apollo: {
		quotesQuery: {
			query: () => queries.SEARCH_QUOTES,
			variables() {
				return this.buildQueryVariables();
			},
			update(data) {
				this.loading = false;
				this.totalCount = data.quotes.totalCount;
				this.quotes = data.quotes.items;
				return data.quotes;
			},
			error(error) {
				console.error("Error fetching pending approvals:", error);
				this.loading = false;
			},
		},
		salesReps: {
			query: queries.LIST_SALES_REPS,
			variables: {
				take: 500,
				offset: 0,
			},
			update: (data) => data.salesReps.items,
		},
	},
	computed: {
		salesRepOptions() {
			if (!this.salesReps) return [];
			return [...this.salesReps.filter((rep) => !rep.inactiveDate)];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
	},
	methods: {
		navigateToCustomer(customerId) {
			if (customerId) {
				this.$router.push(`/relationships/customer/${customerId}`);
			}
		},
		formatDate(date) {
			if (!date) return "";
			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},
		formatStatus(status) {
			if (!status) return "";
			const statusMap = {
				PENDING_APPROVAL: "Pending Approval",
				INCOMPLETE: "Incomplete",
				FINAL_CHECK: "Final Check",
				ORDERED: "Ordered",
				CANCELED: "Canceled",
				CANCELLED: "Cancelled",
				PENDING: "Pending",
				INPROCESS: "In Process",
				SHIPPED: "Shipped",
			};
			return statusMap[status] || status;
		},
		renderPrice,
		repSalesRepInitials(salesRepId) {
			const repById = this.salesReps.find((obj) => obj.salesRepId == salesRepId);
			if (!repById) return "";
			const tokens = repById.name.split(/\s+/).filter((token) => token.length > 0);
			if (tokens.length === 1) {
				return tokens[0];
			}
			const initials = tokens.map((token) => token[0].toUpperCase());
			return initials.join("");
		},
		renderStatusColor(statusLabel) {
			const statusColors = {
				PENDING_APPROVAL: "table-status-blue",
				INCOMPLETE: "table-status-orange",
				FINAL_CHECK: "table-status-light-blue",
				CANCELED: "table-status-red",
				CANCELLED: "table-status-red",
				ORDERED: "table-status-green",
				PENDING: "table-status-orange",
				INPROCESS: "table-status-orange",
				SHIPPED: "table-status-green",
			};
			return statusColors[statusLabel] || "";
		},
		navigateToQuote(quoteData) {
			this.$router.push(`/sales-orders/pending-approvals/${quoteData.quoteId}`);
		},
		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},
		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},
		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},
		onFilterClear() {
			this.$nextTick(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			});
		},
		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				organization: "quoteCustomer.customer.organization",
				quoteNumber: "quoteNumber",
				createdAt: "createdAt",
				salesRepId: "salesRepId",
				status: "status",
				referenceNumber: "referenceNumber",
				primaryContact: "quoteCustomer.contact.lastName",
				email: "quoteCustomer.contact.email",
				total: "total",
				balance: "balance",
				modifiedAt: "modifiedAt",
			};
			return sortFieldMap[columnValue] || "modifiedAt";
		},

		// Build nested sort object from dot notation (e.g., "quoteCustomer.customer.organization")
		buildSortObject(fieldPath, direction) {
			const parts = fieldPath.split(".");
			if (parts.length === 1) {
				return { [parts[0]]: direction };
			}
			// Build nested object from inside out
			let result = { [parts[parts.length - 1]]: direction };
			for (let i = parts.length - 2; i >= 0; i--) {
				result = { [parts[i]]: result };
			}
			return result;
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build where clause - only PENDING_APPROVAL status
			const where = {
				and: [
					{
						or: [
							{ referenceNumber: { contains: keyword } },
							{ quoteNumber: { contains: keyword } },
							{ quoteCustomer: { customer: { organization: { contains: keyword } } } },
							{ quoteCustomer: { customer: { customerNumber: { contains: keyword } } } },
							{ quoteCustomer: { contact: { firstName: { contains: keyword } } } },
							{ quoteCustomer: { contact: { lastName: { contains: keyword } } } },
							{ quoteCustomer: { contact: { email: { contains: keyword } } } },
						],
					},
					{
						status: { eq: "PENDING_APPROVAL" },
					},
				],
			};

			// Add sales rep filter
			if (this.selectedSalesRepIds && this.selectedSalesRepIds.length > 0) {
				where.and.push({
					salesRepId: { in: this.selectedSalesRepIds },
				});
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "modifiedAt";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldPath = this.getSortFieldName(sortColumn);
			const order = [this.buildSortObject(sortFieldPath, sortDirection)];

			// Determine if we should include all reps' pending approvals
			// For sales reps: include all reps when they've selected a rep filter OR when searching with keywords
			// For admins: always include all reps
			const user = this.$store.getters.userInfo;
			const isAdmin = this.$userService.isAdmin(user);
			const isSalesRep = this.$userService.hasRole(user, WellKnownRoles.REP);
			const hasRepFilterSelected = Boolean(this.selectedSalesRepIds && this.selectedSalesRepIds.length > 0);
			const hasSearchKeyword = Boolean(keyword && keyword.length > 0);
			const includeAllReps = Boolean(isAdmin || (isSalesRep && (hasRepFilterSelected || hasSearchKeyword)));

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				searchTerm: "",
				where,
				includeAllReps,
			};
		},
		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.quotesQuery) {
				await this.$apollo.queries.quotesQuery.refetch();
			}
		},
	},
	mounted() {
		const user = this.$store.getters.userInfo;
		this.canEditCustomer =
			this.$userService.isAdmin(user) ||
			this.$userService.hasPermission(user, SystemPermissions.Relationships_Customer_ALL);
		// Only admin users can filter by sales rep (reps should only see their own pending approvals)
		this.canFilterByRep = this.$userService.isAdmin(user);
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.pending-approvals-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.pending-approvals-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.pending-approvals-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.pending-approvals-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.pending-approvals-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;

		table {
			table-layout: fixed;
			width: 100%;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}
	// Make rows clickable
	::v-deep tbody tr {
		cursor: pointer;
		transition: background-color 0.2s;

		&:hover {
			background-color: $color-gray-light !important;
		}
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}

	// Edit customer button - visible on row hover
	.edit-customer-btn {
		opacity: 0;
		transition: opacity 0.2s;
	}

	::v-deep tbody tr:hover .edit-customer-btn {
		opacity: 1;
	}

	// Status badge styling (reuse existing)
	.table-status {
		display: inline-block;
		border: 1px solid;
		border-radius: 3px;
		font-size: 1.4rem;
		padding: 0.5rem;
		min-width: 9rem;
		text-align: center;

		&-orange {
			border-color: $color-orange;
			color: $color-orange;
		}

		&-red {
			border-color: red;
			color: red;
		}

		&-green {
			border-color: $color-green;
			color: $color-green;
		}

		&-blue {
			border-color: $color-blue;
			color: $color-blue;
		}

		&-gray {
			border-color: $color-gray-dark;
			color: $color-gray-dark;
		}
	}
}
</style>
